import { html, css } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { BaseVisualization, ReportEvent, VisualizationOptions } from "./baseVisualization";
import { getThemeManager, utils } from "@omegagrid/core";
import { DeepPartial } from "../../types";
import { loader } from "@omegagrid/plugin-charts";
import { ReportResult } from "../../model";
import { ContextData, GaugeStyle, GaugeViewOptions, ReportContext } from "@reporting/rp-types/analytics";
import { GridModel } from "@omegagrid/grid";
import { createGridModel, evaluateExpression, formatColor, getPlotBandLabel, getPlotBands } from "./viewOptions";
import { getChartTheme, getPlotBandColor, getPlotBandLabelStyle, getThemeColor } from "./chartTheme";
import type { Options, Chart } from 'highcharts';

const DEFAULT_CHART_OPTIONS: DeepPartial<Options> = {
	chart: {
		type: 'gauge',
		styledMode: false,
	},
	pane: {
		startAngle: -90,
		endAngle: 90,
		background: null
	},
	credits: {
		enabled: false
	},
	title: {
		text: null
	}
};

const PANE_ANGLES: Record<string, {startAngle: number, endAngle: number}> = {
	'half': {startAngle: -90, endAngle: 90},
	'three-quarter': {startAngle: -135, endAngle: 135},
	'full': {startAngle: 0, endAngle: 360},
};

@customElement('rp-visual-gauge')
export class GaugeVisualization extends BaseVisualization {

	static styles = [BaseVisualization.styles, css`
		:host {
			width: 100%;
			height: 100%;
			position: relative;
			overflow: hidden;
		}

		.gauge {
			position: absolute;
			top: 0;
			left: 0;
			right: -1px;
			bottom: 0;
		}
	`];

	@query('.gauge')
	wrapper: HTMLDivElement;

	@property({type: Object})
	result: ReportResult;

	@property({type: Object})
	reportContext: ReportContext;

	private _gridModel: GridModel;

	private chart: Chart;

	get gridModel() {
		return this._gridModel ??= createGridModel(this.result, this.reportContext);
	}

	private evaluateExpression(value: string|number) : string|number {
		return evaluateExpression(value, () => this.gridModel);
	}

	get viewOptions(): GaugeViewOptions {
		return (this.contextData?.view_options ?? {}) as GaugeViewOptions;
	}

	/** user defined plot bands with evaluated from/to expressions */
	getPlotBands() {
		return getPlotBands(this.viewOptions.plot_bands, this.viewOptions.plot_bands_defaults, () => this.gridModel);
	}

	get gaugeStyle(): GaugeStyle {
		return this.viewOptions.style ?? {};
	}

	createChartOptions() : DeepPartial<Options> {
		const style = this.gaugeStyle;
		const valueFormat = `{y:.${style.value_decimals ?? 0}f}${style.value_format ?? ''}`;

		const options: DeepPartial<Options> = {
			pane: style.angle ? PANE_ANGLES[style.angle] : {},
			series: [{
				data: [this.evaluateExpression(this.viewOptions.value)],
				// geometry as percentages of the pane radius so the dial scales
				// with the rest of the gauge (zoom/resize); px-based baseWidth is
				// fine — Highcharts scales the whole path with the pane
				dial: {
					radius: '90%',
					baseLength: '0%',
					rearLength: '8%',
					baseWidth: style.dial_width ?? '20%',
					topWidth: 3,
					borderRadius: '9%',
					...(style.dial_color ? {backgroundColor: formatColor(style.dial_color)} : {}),
				},
				...(style.pivot_color ? {pivot: {backgroundColor: formatColor(style.pivot_color)}} : {}),
				dataLabels: {
					enabled: style.value_label !== false,
					format: valueFormat,
					style: {
						...(style.value_color ? {color: formatColor(style.value_color)} : {}),
						...(style.value_size ? {fontSize: `${style.value_size}px`} : {}),
					},
				}
			}],
			yAxis: {
				min: Number(this.evaluateExpression(this.viewOptions.min)),
				max: Number(this.evaluateExpression(this.viewOptions.max)),
				tickInterval: style.tick_interval || undefined,
				// the modern gauge defaults (highcharts 12+) shift the axis to an
				// inner ring via a computed offset, leaving the tick marks
				// floating detached in the middle; a zero offset pins them back
				// to the rim where they cut into the bands. No outer arc line.
				offset: 0,
				lineWidth: 0,
				tickWidth: 2,
				tickLength: 12,
				tickColor: getThemeColor(this, 'highcharts-neutral-color-80'),
				minorTicks: style.minor_ticks !== false,
				minorTickWidth: 1,
				minorTickLength: 6,
				minorTickColor: getThemeColor(this, 'highcharts-neutral-color-60'),
				labels: {
					enabled: style.labels !== false,
					format: `{value}${style.labels_format ?? ''}`,
				},
				// a band without an own color gets the subtle theme fill
				plotBands: this.getPlotBands().map(band => ({
					from: band.from,
					to: band.to,
					color: band.color ? formatColor(band.color) : getPlotBandColor(this),
					thickness: style.band_thickness ? `${style.band_thickness}%` : undefined,
					label: getPlotBandLabel(band.label, getPlotBandLabelStyle(this)),
				}))
			}
		};

		// the raw highcharts options of the user (the json tab of the editor)
		// merge as the last step - they win over everything generated
		return utils.mergeDeep({}, getChartTheme(this), DEFAULT_CHART_OPTIONS, options, this.viewOptions.highchart_options ?? {});
	}

	refresh(contextData?: ContextData) {
		super.refresh(contextData);
		if (contextData) {
			this.requestUpdate();
			this.dispatchEditEvent();
		}
	}

	willUpdate(props: Map<string, any>) {
		this._gridModel = null;
	}

	/** theme colors are baked into the rendered svg, a theme switch re-renders */
	private readonly onThemeChange = () => this.requestUpdate();

	connectedCallback() {
		super.connectedCallback();
		getThemeManager().addEventListener('change', this.onThemeChange);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		getThemeManager().removeEventListener('change', this.onThemeChange);
	}

	async updated(props: Map<string, any>) {
		this.chart = null;
		const Highcharts = await loader.get();
		const options = this.createChartOptions();
		this.chart = Highcharts.chart(this.wrapper as HTMLElement, options);

		if (props.has('chartData')) {
			this.refresh();
			this.dispatchEvent(new ReportEvent('load', {visualization: this}));
		}
	}

	layout() {
		this.chart?.reflow();
	}

	render = () => html`<div class="gauge"></div>`;

	getOptions(): VisualizationOptions {
		return this.createOptions({edit: true});
	}

}
